"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Check, X, Minus, ChevronDown, ChevronUp, Bug } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "~/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from "~/components/ui/table";
import { simulateCoachMatchScore } from "~/lib/functions/matching-debug";
import {
  CoachFinderSchema,
  type CoachFinderForm,
  goalsField,
  coachGenderField,
  coachAgesField,
  coachingPackagesField,
  coachingApproachesField,
  faithIntegrationsField,
  coachPersonalityField,
  sessionDeliveryMethodsFeild,
} from "~/lib/schemas/coach-discovery";
import {
  type CoachMatchingDebugData,
  type SimulatedMatchScore,
  SCORE_DISPLAY_ORDER,
  WEIGHT_LABELS,
} from "~/lib/schemas/coach-matching-debug";

interface CoachMatchingDebugProps {
  data: CoachMatchingDebugData;
  coachId: string;
}

function StatusIndicator({ value }: { value: boolean | null }) {
  if (value === null) {
    return <Minus className="h-4 w-4 text-gray-400" />;
  }
  return value ? (
    <Check className="h-4 w-4 text-green-600" />
  ) : (
    <X className="h-4 w-4 text-red-600" />
  );
}

function DataRow({
  label,
  value,
  isStatus,
}: {
  label: string;
  value: React.ReactNode;
  isStatus?: boolean;
}) {
  return (
    <div className="flex items-center justify-between border-b border-gray-100 py-1.5 last:border-0">
      <span className="text-sm text-gray-600">{label}</span>
      {isStatus ? (
        <StatusIndicator value={value as boolean} />
      ) : (
        <span className="font-mono text-sm">{value ?? "-"}</span>
      )}
    </div>
  );
}

function Section({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between rounded bg-gray-50 px-2 py-1.5 text-left text-sm font-medium hover:bg-gray-100">
        {title}
        {open ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="px-2 pt-2">{children}</CollapsibleContent>
    </Collapsible>
  );
}

function ScoreSimulator({
  coachId,
  coachData: _coachData,
}: {
  coachId: string;
  coachData: CoachMatchingDebugData;
}) {
  const [result, setResult] = React.useState<SimulatedMatchScore | null>(null);

  const { register, handleSubmit } = useForm<CoachFinderForm>({
    resolver: zodResolver(CoachFinderSchema),
    defaultValues: {
      goals: ["Career & Business Coaching"],
      coachingFormats: "any",
      sessionDeliveryMethods: "No preference",
      coachGender: "No preference",
      coachAges: ["No preference"],
      coachingApproaches: [],
      coachingPackages: [],
      faithIntegration: [],
      coachPersonality: [],
      preferredLanguages: [],
      prefersCoachLocation: "No preference",
    },
  });

  const simulate = useMutation({
    mutationFn: (preferences: CoachFinderForm) =>
      simulateCoachMatchScore({ data: { coachId, preferences } }),
    onSuccess: (data) => {
      setResult(data);
    },
  });

  const onSubmit = (data: CoachFinderForm) => {
    simulate.mutate(data);
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-medium text-gray-700">Goals</label>
            <select
              {...register("goals.0")}
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-xs"
            >
              {goalsField.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">Gender</label>
            <select
              {...register("coachGender")}
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-xs"
            >
              {coachGenderField.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">
              Age Range
            </label>
            <select
              {...register("coachAges.0")}
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-xs"
            >
              {coachAgesField.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">
              Experience
            </label>
            <select
              {...register("coachingPackages.0")}
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-xs"
            >
              <option value="">No preference</option>
              {coachingPackagesField.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">
              Approach
            </label>
            <select
              {...register("coachingApproaches.0")}
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-xs"
            >
              <option value="">No preference</option>
              {coachingApproachesField.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">Faith</label>
            <select
              {...register("faithIntegration.0")}
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-xs"
            >
              {faithIntegrationsField.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">
              Personality
            </label>
            <select
              {...register("coachPersonality.0")}
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-xs"
            >
              <option value="">No preference</option>
              {coachPersonalityField.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700">
              Session Format
            </label>
            <select
              {...register("sessionDeliveryMethods")}
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-xs"
            >
              {sessionDeliveryMethodsFeild.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <Button
          type="submit"
          size="sm"
          className="w-full"
          disabled={simulate.isPending}
        >
          {simulate.isPending ? "Calculating..." : "Simulate Score"}
        </Button>
      </form>

      {result && (
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded bg-blue-50 px-3 py-2">
            <span className="text-sm font-medium">Normalized Score</span>
            <span className="text-xl font-bold text-blue-700">
              {Math.round(result.normalizedScore * 100)}%
            </span>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Component</TableHead>
                <TableHead className="text-right text-xs">Score</TableHead>
                <TableHead className="text-right text-xs">Weight</TableHead>
                <TableHead className="text-right text-xs">Weighted</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {SCORE_DISPLAY_ORDER.map((key) => (
                <TableRow key={key}>
                  <TableCell className="py-1 text-xs">
                    {WEIGHT_LABELS[key]}
                  </TableCell>
                  <TableCell className="py-1 text-right font-mono text-xs">
                    {result.scores[key].toFixed(2)}
                  </TableCell>
                  <TableCell className="py-1 text-right font-mono text-xs text-gray-500">
                    {result.weights[key].toFixed(1)}
                  </TableCell>
                  <TableCell className="py-1 text-right font-mono text-xs">
                    {(result.scores[key] * result.weights[key]).toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell className="py-1 text-xs font-medium">
                  Total
                </TableCell>
                <TableCell className="py-1 text-right text-xs">-</TableCell>
                <TableCell className="py-1 text-right font-mono text-xs text-gray-500">
                  {Object.values(result.weights)
                    .reduce((a, b) => a + b, 0)
                    .toFixed(1)}
                </TableCell>
                <TableCell className="py-1 text-right font-mono text-xs font-medium">
                  {result.totalWeighted.toFixed(2)}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>

          <div className="rounded border border-gray-200 p-2">
            <div className="mb-1 text-xs font-medium text-gray-700">
              Hard Filters
            </div>
            <div className="grid grid-cols-3 gap-1">
              {(
                Object.entries(result.hardFilters) as [
                  keyof typeof result.hardFilters,
                  boolean,
                ][]
              ).map(([key, passes]) => (
                <div key={key} className="flex items-center gap-1 text-xs">
                  <StatusIndicator value={passes} />
                  <span className="capitalize">{key}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CoachMatchingDebug({ data, coachId }: CoachMatchingDebugProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="fixed right-4 bottom-4 z-50">
      {!isOpen ? (
        <Button
          onClick={() => setIsOpen(true)}
          size="sm"
          className="gap-2 bg-yellow-50 text-yellow-800 hover:bg-yellow-100"
        >
          <Bug className="h-4 w-4" />
          Matching Debug
        </Button>
      ) : (
        <Card className="max-h-[80vh] w-[400px] overflow-y-auto shadow-xl">
          <CardHeader className="sticky top-0 z-10 bg-white pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Bug className="h-4 w-4 text-yellow-600" />
                Matching Debug
              </CardTitle>
              <Button
                onClick={() => setIsOpen(false)}
                size="sm"
                variant="ghost"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <Badge
              variant="outline"
              className="w-fit bg-yellow-50 text-yellow-800"
            >
              Development Only
            </Badge>
          </CardHeader>
          <CardContent className="space-y-3 pt-0">
            <Section title="Eligibility">
              <DataRow
                label="Profile Photo"
                value={data.eligibility.hasProfilePhoto}
                isStatus
              />
              <DataRow
                label="GHL Integration"
                value={data.eligibility.hasGhlIntegration}
                isStatus
              />
              <DataRow
                label="Public Profile"
                value={data.eligibility.isPublic}
                isStatus
              />
              <DataRow
                label="Has Embedding"
                value={data.hasEmbedding}
                isStatus
              />
            </Section>

            <Section title="Demographics">
              <DataRow label="Gender" value={data.demographics.gender} />
              <DataRow label="Age" value={data.demographics.age} />
              <DataRow label="City" value={data.demographics.primaryCity} />
              <DataRow
                label="Major City"
                value={data.demographics.closestMajorCity}
              />
              <DataRow
                label="Country"
                value={data.demographics.primaryCountry}
              />
              <DataRow label="Languages" value={data.demographics.languages} />
            </Section>

            <Section title="Expertise" defaultOpen={false}>
              <div className="space-y-2">
                <div>
                  <div className="text-xs font-medium text-gray-500">
                    Primary ({data.expertise.primary.length})
                  </div>
                  {data.expertise.primary.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {data.expertise.primary.map((e) => (
                        <Badge
                          key={e.id}
                          variant="secondary"
                          className="text-xs"
                        >
                          {e.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">None</span>
                  )}
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500">
                    Secondary ({data.expertise.secondary.length})
                  </div>
                  {data.expertise.secondary.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {data.expertise.secondary.map((e) => (
                        <Badge key={e.id} variant="outline" className="text-xs">
                          {e.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">None</span>
                  )}
                </div>
              </div>
            </Section>

            <Section title="Coaching" defaultOpen={false}>
              <DataRow
                label="Formats"
                value={data.coaching.formats.join(", ") || "-"}
              />
              <DataRow
                label="Delivery"
                value={data.coaching.sessionDeliveryMethods.join(", ") || "-"}
              />
              <DataRow
                label="Approaches"
                value={data.coaching.coachingApproaches.join(", ") || "-"}
              />
              <DataRow label="Level" value={data.coaching.experienceLevel} />
            </Section>

            <Section title="Faith & Personality" defaultOpen={false}>
              <DataRow label="Faith" value={data.faith.join(", ") || "-"} />
              <DataRow label="DISC" value={data.personality.discType} />
              <DataRow label="MBTI" value={data.personality.mbtiType} />
              <DataRow
                label="Enneagram"
                value={data.personality.enneagramType}
              />
              <DataRow
                label="Human Design"
                value={data.personality.humanDesignType}
              />
            </Section>

            <Section title="Status">
              <DataRow
                label="Founding Coach"
                value={data.status.isFoundingCoach}
                isStatus
              />
              <DataRow
                label="Featured Coach"
                value={data.status.isFeaturedCoach}
                isStatus
              />
            </Section>

            <Section title="Score Simulator" defaultOpen={true}>
              <ScoreSimulator coachId={coachId} coachData={data} />
            </Section>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
